import { ArrowUpRight, FileCheck2, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../lib/usePageMeta";

const DOCUMENTS = [
  ["Privacy Policy", "How HUMN collects, uses and protects personal information.", "/privacy", true],
  ["User Agreement & Terms", "The agreement governing access to and use of HUMN.", "/terms", true],
  ["HumnAi Policy", "AI intelligence, safety and action governance framework.", "/humnai-policy", true],
  ["Credits Policy", "Credits, payments, rewards, escrow and platform economy.", "/credits-policy", true],
  ["Content Policy", "User-generated content, AI content and moderation standards.", "/content-policy", true],
  ["Community Guidelines", "How HUMN keeps community participation human, useful and safe.", "/community-guidelines", true],
  ["Safety Policy", "Child, personal, AI, transaction and platform safety standards.", "/safety-policy", true],
  ["Creator Policy", "Standards for creators, offerings and fulfilment.", "/creator-policy", true],
  ["Cookie Policy", "How the HUMN website uses cookies and similar technology.", "/cookie-policy", true],
  ["Refund Policy", "Refund eligibility, timing and processing principles.", "/refund-policy", true],
];

const MASTER_DOCUMENT = ["Master Governance, Platform Protection & Rights Policy", "The overriding governance framework for HUMN, HumnAi, platform integrity, technology, rights and lawful protection.", "/master-policy"];

export default function PoliciesPage() {
  usePageMeta("Policies & Legal — HUMN", "The complete HUMN policy and legal document library.");

  return (
    <main className="scene scene-purple min-h-screen pt-36 pb-28 px-6" data-testid="policies-page">
      <div className="max-w-5xl mx-auto">
        <p className="eyebrow mb-5">HUMN · Trust centre</p>
        <div className="max-w-3xl">
          <h1 className="h-display h-lg" data-testid="policies-page-title">Policies & legal</h1>
          <p className="tx-2 text-base sm:text-lg leading-relaxed mt-6" data-testid="policies-page-intro">
            One place for the rules, protections and governance frameworks behind HUMN and HumnAi.
          </p>
        </div>

        <Link
          to={MASTER_DOCUMENT[2]}
          className="master-policy-feature group mt-14 grid sm:grid-cols-[minmax(0,1fr)_auto] gap-8 items-end p-7 sm:p-10"
          data-testid="policy-directory-link-master-policy"
        >
          <span>
            <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-white/55">Highlighted · Master governance</span>
            <strong className="block text-2xl sm:text-4xl font-semibold text-white mt-5 max-w-3xl">{MASTER_DOCUMENT[0]}</strong>
            <span className="block text-sm sm:text-base text-white/60 mt-4 max-w-2xl leading-relaxed">{MASTER_DOCUMENT[1]}</span>
          </span>
          <span className="w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1">
            <ArrowUpRight aria-hidden="true" />
          </span>
        </Link>

        <div className="mt-8 border-t" style={{ borderColor: "var(--border-glass)" }} data-testid="policies-directory">
          {DOCUMENTS.map(([title, description, path, official], index) => (
            <Link
              key={path}
              to={path}
              className="group grid sm:grid-cols-[3rem_minmax(0,1fr)_auto] gap-4 sm:gap-6 items-center py-6 sm:py-8 border-b transition-colors duration-200 hover:bg-white/[0.025]"
              style={{ borderColor: "var(--border-glass)" }}
              data-testid={`policy-directory-link-${path.slice(1)}`}
            >
              <span className="font-mono-ui text-xs tx-3">{String(index + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-3">
                  <strong className="text-xl sm:text-2xl font-semibold tx-1">{title}</strong>
                  <span className="chip text-[10px] px-2 py-1 inline-flex items-center gap-1.5">
                    {official ? <FileCheck2 size={12} aria-hidden="true" /> : <FileText size={12} aria-hidden="true" />}
                    {official ? "Official" : "Policy"}
                  </span>
                </span>
                <span className="block tx-2 text-sm sm:text-base mt-2">{description}</span>
              </span>
              <ArrowUpRight className="hidden sm:block tx-3 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}